/**
 * Layout.jsx — ELYON-SHELL-FOUNDATION-1
 *
 * CSS grid wrapper: sidebar (fixed width, collapsible) + top bar + main area.
 * Sidebar width driven by userPrefsStore.sidebarWidth and collapsed state.
 * No hard-coded dimensions — CSS variables from elyon-tokens.css.
 */
import { useEffect } from 'react';
import { Sidebar }  from './Sidebar.jsx';
import { TopBar }   from './TopBar.jsx';
import { useSidebarCollapsed, useSidebarHidden, useSidebarWidth, useUserPrefsStore } from '../stores/userPrefsStore.js';

/**
 * @param {{ children: import('react').ReactNode }} props
 */
export function Layout({ children }) {
  const isCollapsed = useSidebarCollapsed();
  const isHidden    = useSidebarHidden();
  const sidebarWidth = useSidebarWidth();
  const { applyToDocument } = useUserPrefsStore();

  // Apply persisted prefs to document on mount
  useEffect(() => {
    applyToDocument();
  }, [applyToDocument]);

  const layoutClass = [
    'elyon-layout',
    isCollapsed && 'sidebar-collapsed',
    isHidden    && 'sidebar-hidden',
  ].filter(Boolean).join(' ');

  // Sync CSS variable with persisted width
  const cssVars = !isCollapsed && !isHidden
    ? { '--sidebar-width': `${sidebarWidth}px` }
    : undefined;

  return (
    <div
      className={layoutClass}
      style={cssVars}
      data-testid="elyon-layout"
    >
      <Sidebar />
      <TopBar />
      <main
        className="elyon-main"
        id="main-content"
        aria-label="Main content"
        tabIndex={-1}
      >
        {children}
      </main>
    </div>
  );
}
